import React, { useEffect, useMemo, useState } from "react";
import * as Babel from "@babel/standalone";
import { collection, getDocs } from "firebase/firestore";
import { getBytes, ref } from "firebase/storage";
import { db, storage } from "./firebase";

const textDecoder = new TextDecoder();

function bytesToText(bytes) {
  return textDecoder.decode(bytes);
}

function stripTailwindDirectives(cssText = "") {
  return cssText
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      return (
        trimmed !== "@tailwind base;" &&
        trimmed !== "@tailwind components;" &&
        trimmed !== "@tailwind utilities;"
      );
    })
    .join("\n");
}

function normalizePath(path) {
  return path.replace(/\\/g, "/");
}

function resolveRelativePath(fromPath, importPath) {
  const fromParts = normalizePath(fromPath).split("/");
  fromParts.pop();

  const importParts = normalizePath(importPath).split("/");
  const combined = [...fromParts];

  for (const part of importParts) {
    if (!part || part === ".") continue;
    if (part === "..") {
      combined.pop();
    } else {
      combined.push(part);
    }
  }

  return combined.join("/");
}

function resolveLocalImport(fromPath, importPath, availableFiles) {
  const baseResolved = resolveRelativePath(fromPath, importPath);

  const candidates = [
    baseResolved,
    `${baseResolved}.js`,
    `${baseResolved}.jsx`,
    `${baseResolved}.css`,
    `${baseResolved}/index.js`,
    `${baseResolved}/index.jsx`,
  ];

  for (const candidate of candidates) {
    if (availableFiles.has(candidate)) return candidate;
  }

  return baseResolved;
}

function walkFolderTree(node, output = []) {
  if (!node) return output;

  if (node.files) {
    for (const [, fileMeta] of Object.entries(node.files)) {
      if (fileMeta?.fileRole === "placeholder") continue;
      if (fileMeta?.name === "_keep") continue;
      output.push(fileMeta);
    }
  }

  if (node.folders) {
    for (const [, folderNode] of Object.entries(node.folders)) {
      walkFolderTree(folderNode, output);
    }
  }

  return output;
}

async function loadCodeStructure({ userId, websiteId }) {
  console.log("[preview] loadCodeStructure start");

  const codeColRef = collection(
    db,
    "workspaces",
    userId,
    "websites",
    websiteId,
    "code"
  );

  const snap = await getDocs(codeColRef);

  console.log("[preview] loadCodeStructure snapshot size", snap.size);

  const rootDocs = {};
  snap.forEach((docSnap) => {
    rootDocs[docSnap.id] = docSnap.data();
  });

  console.log("[preview] loadCodeStructure done", rootDocs);

  return rootDocs;
}

async function loadProjectFilesFromStorage(rootDocs) {
  console.log("[preview] loadProjectFilesFromStorage start", rootDocs);

  const fileList = [];

  Object.values(rootDocs).forEach((rootDoc) => {
    walkFolderTree(rootDoc, fileList);
  });

  console.log("[preview] flattened fileList", fileList);

  const uniqueFiles = Array.from(
    new Map(fileList.map((file) => [file.relativePath, file])).values()
  );

  console.log("[preview] unique files", uniqueFiles);

  const fileContents = {};

  for (const file of uniqueFiles) {
    console.log("[preview] reading storage file", file.fullPath);

    try {
      const fileRef = ref(storage, file.fullPath);
      const bytes = await getBytes(fileRef);

      console.log("[preview] got bytes for", file.relativePath, bytes?.length);

      fileContents[file.relativePath] = {
        ...file,
        content: bytesToText(bytes),
      };
    } catch (error) {
      console.error("[preview] failed storage read", file.fullPath, error);
      throw new Error(`Failed to load file: ${file.relativePath}`);
    }
  }

  console.log("[preview] loadProjectFilesFromStorage done", fileContents);

  return fileContents;
}

function collectCssImports(code, filePath, availableFiles, collectedCssPaths) {
  return code.replace(
    /import\s+["'](.+?\.css)["'];?/g,
    (_, importPath) => {
      const resolvedCssPath = resolveLocalImport(filePath, importPath, availableFiles);
      collectedCssPaths.add(resolvedCssPath);
      return "";
    }
  );
}

function rewriteLocalImports(code, filePath, availableFiles) {
  let nextCode = code;

  nextCode = nextCode.replace(
    /from\s+["'](\.\/.*?|\.\.\/.*?)["']/g,
    (_, importPath) => {
      const resolved = resolveLocalImport(filePath, importPath, availableFiles);
      return `from "virtual:/${resolved}"`;
    }
  );

  nextCode = nextCode.replace(
    /import\(\s*["'](\.\/.*?|\.\.\/.*?)["']\s*\)/g,
    (_, importPath) => {
      const resolved = resolveLocalImport(filePath, importPath, availableFiles);
      return `import("virtual:/${resolved}")`;
    }
  );

  return nextCode;
}

function transpileJsxModule(code, filePath) {
  const result = Babel.transform(code, {
    presets: [["react", { runtime: "classic" }]],
    sourceType: "module",
    filename: filePath,
  });

  return result.code || "";
}

function buildImportMap(virtualModuleMap) {
  return {
    imports: {
      react: "https://esm.sh/react@18",
      "react-dom/client": "https://esm.sh/react-dom@18/client",
      "firebase/app": "https://esm.sh/firebase@12.8.0/app",
      "firebase/auth": "https://esm.sh/firebase@12.8.0/auth",
      "firebase/firestore": "https://esm.sh/firebase@12.8.0/firestore",
      "firebase/storage": "https://esm.sh/firebase@12.8.0/storage",
      ...virtualModuleMap,
    },
  };
}

function buildPreviewHtml({ importMap, entryPath, cssText }) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
${cssText}
    </style>
    <script type="importmap">
${JSON.stringify(importMap, null, 2)}
    </script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module">
      import "virtual:/${entryPath}";
    </script>
  </body>
</html>`;
}

async function buildCodePreviewDocument({ userId, websiteId }) {
  console.log("[preview] 1. start buildCodePreviewDocument", { userId, websiteId });

  const rootDocs = await loadCodeStructure({ userId, websiteId });
  console.log("[preview] 2. loaded rootDocs", rootDocs);

  const fileMap = await loadProjectFilesFromStorage(rootDocs);
  console.log("[preview] 3. loaded fileMap", fileMap);

  const availableFiles = new Set(Object.keys(fileMap));
  const collectedCssPaths = new Set();
  const compiledModules = {};

  for (const [filePath, fileData] of Object.entries(fileMap)) {
    const isCss = filePath.endsWith(".css");
    if (isCss) continue;

    let code = fileData.content || "";

    code = collectCssImports(code, filePath, availableFiles, collectedCssPaths);
    code = transpileJsxModule(code, filePath);
    code = rewriteLocalImports(code, filePath, availableFiles);

    compiledModules[filePath] = code;
  }

  console.log("[preview] 4. compiled modules", compiledModules);
  console.log("[preview] 5. collected css paths", Array.from(collectedCssPaths));

  let combinedCss = "";

  for (const cssPath of collectedCssPaths) {
    const cssFile = fileMap[cssPath];
    if (cssFile?.content) {
      combinedCss += `\n${stripTailwindDirectives(cssFile.content)}\n`;
    }
  }

  const virtualModuleMap = {};
  const blobUrls = [];

  for (const [filePath, compiledCode] of Object.entries(compiledModules)) {
    const blob = new Blob([compiledCode], { type: "text/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    virtualModuleMap[`virtual:/${filePath}`] = blobUrl;
    blobUrls.push(blobUrl);
  }

  const importMap = buildImportMap(virtualModuleMap);

  const srcDoc = buildPreviewHtml({
    importMap,
    entryPath: "src/main.jsx",
    cssText: combinedCss,
  });

  console.log("[preview] 6. finished srcDoc build");

  return {
    srcDoc,
    blobUrls,
    rootDocs,
    fileMap,
  };
}

export function CodeProjectPreview({
    userId,
    websiteId,
    iframeRef,
    title = "Kairos Preview",
  }) {
    const [srcDoc, setSrcDoc] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
  
    useEffect(() => {
      let mounted = true;
      let createdBlobUrls = [];
  
      async function loadPreview() {
        try {
          setLoading(true);
          setError("");
          setSrcDoc("");
  
          console.log("Loading preview for:", { userId, websiteId });
  
          const result = await buildCodePreviewDocument({ userId, websiteId });
  
          console.log("Preview build result:", result);
  
          createdBlobUrls = result?.blobUrls || [];
  
          if (mounted) {
            setSrcDoc(result?.srcDoc || "");
          }
        } catch (err) {
          console.error("Code preview build error:", err);
  
          if (mounted) {
            setError(err?.message || "Failed to build preview.");
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      }
  
      if (userId && websiteId) {
        loadPreview();
      } else {
        setLoading(false);
        setError("Missing userId or websiteId");
      }
  
      return () => {
        mounted = false;
        createdBlobUrls.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [userId, websiteId]);
  
    if (loading) {
      return (
        <div className="render-placeholder">
          <div className="render-placeholder-title">Loading preview…</div>
          <div className="render-placeholder-subtitle">
            Preparing your website files...
          </div>
        </div>
      );
    }
  
    if (error) {
      return (
        <div className="render-placeholder">
          <div className="render-placeholder-title">Preview failed</div>
          <div className="render-placeholder-subtitle">{error}</div>
        </div>
      );
    }
  
    if (!srcDoc) {
      return (
        <div className="render-placeholder">
          <div className="render-placeholder-title">No preview document</div>
          <div className="render-placeholder-subtitle">
            The renderer did not generate iframe content.
          </div>
        </div>
      );
    }
  
    return (
      <iframe
        ref={iframeRef}
        title={title}
        style={{ width: "100%", height: "100%", border: "0" }}
        sandbox="allow-same-origin allow-scripts allow-forms"
        srcDoc={srcDoc}
      />
    );
  }